import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";

const TicketOptionsMenu = ({
	ticket,
	menuOpen,
	handleClose,
	anchorEl,
	showTransferOption = true,
	showInternalEvents = false,
	onToggleInternalEvents = null,
	extraActions = [],
}) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleToggleInternalEvents = e => {
		if (onToggleInternalEvents) {
			onToggleInternalEvents();
		}
		handleClose();
	};

	const handleCopyTicketLink = async () => {
		const ticketUrl = `${window.location.origin}/tickets/${ticket.id}`;

		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(ticketUrl);
			} else {
				const temporaryInput = document.createElement("textarea");
				temporaryInput.value = ticketUrl;
				temporaryInput.setAttribute("readonly", "");
				temporaryInput.style.position = "absolute";
				temporaryInput.style.left = "-9999px";
				document.body.appendChild(temporaryInput);
				temporaryInput.select();
				document.execCommand("copy");
				document.body.removeChild(temporaryInput);
			}

			toast.success(i18n.t("ticketOptionsMenu.copyLinkSuccess"));
		} catch (err) {
			toastError(err);
		} finally {
			handleClose();
		}
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				{extraActions.map(action => (
					<MenuItem
						key={action.key || action.label}
						disabled={action.disabled}
						onClick={event => {
							action.onClick(event);
							handleClose();
						}}
					>
						{action.label}
					</MenuItem>
				))}
				{onToggleInternalEvents && (
					<MenuItem onClick={handleToggleInternalEvents}>
						{showInternalEvents
							? i18n.t("messagesList.internalEvents.hide")
							: i18n.t("messagesList.internalEvents.show")}
					</MenuItem>
				)}
				<MenuItem onClick={handleCopyTicketLink}>
					{i18n.t("ticketOptionsMenu.copyLink")}
				</MenuItem>
				{showTransferOption && (
					<MenuItem onClick={handleOpenTransferModal}>
						{i18n.t("ticketOptionsMenu.transfer")}
					</MenuItem>
				)}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
				ticketWhatsappId={ticket.whatsappId}
			/>
		</>
	);
};

export default TicketOptionsMenu;
